import { Fragment, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { Divider, Icon, Skeleton } from '../../../../core-components';

import type { IconName} from '../../../../core-components';
import type { FC, ReactNode} from 'react';

export type TableItem = { icon: IconName; key: ReactNode; value: ReactNode };

const ItemDivider: FC<{
  align: 'horizontal' | 'vertical';
  className?: string;
}> = ({ align, className }) => (
  <Divider
    align={align}
    className={twMerge(align === 'vertical' && 'h-auto', className)}
  />
);

const Item: FC<{ item: TableItem; fallback?: boolean }> = ({
  item,
  fallback,
}) => {
  return (
    <div className="flex gap-2 items-center w-full">
      {fallback ? (
        <Skeleton className="grow h-5" />
      ) : (
        <>
          <Icon name={item?.icon} className="text-marine-100" size="sm" />
          <span className="grow md:grow-0">{item?.key}</span>
          <span className="text-sm text-medium-emphasis ml-auto">
            {item?.value}
          </span>
        </>
      )}
    </div>
  );
};

const ItemGroup: FC<{
  item1?: TableItem;
  item2?: TableItem;
  fallback?: boolean;
}> = ({ fallback, item1, item2 }) => {
  return (
    <div className="flex flex-col md:flex-row w-full gap-4">
      {item1 && <Item item={item1} fallback={fallback} />}
      {item2 && (
        <>
          <ItemDivider align="vertical" className="hidden md:block" />
          <ItemDivider align="horizontal" className="md:hidden" />
          <Item item={item2} fallback={fallback} />
        </>
      )}
    </div>
  );
};

/**
 * This component displays a grid of key-value pairs surrounded by a border.
 *
 * Based on the viewport size, the grid will only have
 * one row (mobile), two rows (tablet) or four rows (desktop).
 */
export const DashboardStageOverviewTable: FC<{
  items?: TableItem[];
  fallback?: boolean;
}> = ({ items = [], fallback }) => {
  /**
   * The key-value pair items are grouped into tuples of two inside another tuple of two.
   *
   * [[Item, Item], [Item, Item]]
   *
   * By grouping the items like this, its easier to render the grid and let css take care of the layout.
   */
  const grouped = useMemo(() => {
    const itemsOrFallback = fallback
      ? Array(4)
          .fill(null)
          .map<TableItem>((_, index) => ({
            id: `${index}-1`,
            icon: 'loading',
            key: '',
            value: '',
          }))
      : items;

    const grouped: [
      [TableItem | undefined, TableItem | undefined],
      [TableItem | undefined, TableItem | undefined],
    ][] = [];

    for (let i = 0; i < itemsOrFallback.length; i += 4) {
      grouped.push([
        [itemsOrFallback[i], itemsOrFallback[i + 1]],
        [itemsOrFallback[i + 2], itemsOrFallback[i + 3]],
      ]);
    }

    return grouped;
  }, [items, fallback]);

  if (grouped.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {grouped.map(([[item1, item2], [item3, item4]], index) => (
        <Fragment key={index}>
          {index === 0 && <ItemDivider align="horizontal" />}
          {item1 && (
            <>
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                <ItemGroup item1={item1} item2={item2} fallback={fallback} />

                {item3 && (
                  <>
                    <ItemDivider align="vertical" className="hidden lg:block" />
                    <ItemDivider align="horizontal" className="lg:hidden" />
                    <ItemGroup
                      item1={item3}
                      item2={item4}
                      fallback={fallback}
                    />
                  </>
                )}
              </div>
              <ItemDivider align="horizontal" />
            </>
          )}
        </Fragment>
      ))}
    </div>
  );
};
